
import React from 'react';
import { UserProfile, Client } from '@/shared/types';

interface TemplateProps {
  profile: UserProfile;
  client?: Client | null;
  title: string;
  metaData: { label: string; value: string }[];
  children: React.ReactNode;
  footerNote?: string;
  type: string;
  containerClass: string;
  LogoImage: React.FC<{ size: 'sm' | 'md' | 'lg' | 'xl'; className?: string; light?: boolean }>;
  hideHeader?: boolean;
}

const ExecutiveTemplate: React.FC<TemplateProps> = ({
  profile, client, title, metaData, children, footerNote, type, containerClass, LogoImage, hideHeader = false
}) => {
  return (
    <div className={containerClass}>
       {!hideHeader && (
         <>
           {/* Executive Header */}
           <div className="bg-neutral-900 text-white p-12 flex justify-between items-center border-b-4" style={{ borderColor: profile.brandColor }}>
               <div className="flex items-center gap-6">
                   <LogoImage size="md" light />
                   <div className="border-l border-neutral-700 pl-6">
                       <h1 className="text-2xl font-serif font-bold tracking-wide">{profile.name}</h1>
                       <div className="text-xs text-neutral-400 mt-2 space-y-0.5 font-sans">
                           <p>{profile.address}</p>
                           <p>{profile.email} • {profile.phone}</p>
                       </div>
                   </div>
               </div>
               <div className="text-right">
                   <h2 className="text-3xl font-light uppercase tracking-widest opacity-90">{type}</h2>
                   <p className="text-sm text-neutral-400 mt-1 uppercase tracking-wide">{title}</p>
               </div>
           </div>

           {/* Sub-Header Bar */}
           <div className="bg-neutral-100 px-12 py-6 border-b border-neutral-200 grid grid-cols-4 gap-4">
               {metaData.map((m, i) => (
                   <div key={i} className="border-l-2 border-neutral-300 pl-4">
                       <p className="text-[10px] font-bold text-neutral-500 uppercase">{m.label}</p>
                       <p className="text-sm font-bold text-neutral-800">{m.value}</p>
                   </div>
               ))}
           </div>
         </>
       )}

       <div className="p-12 flex-1 flex flex-col">
           <div className="mb-12">
               <p className="text-xs font-bold text-neutral-400 uppercase tracking-widest mb-4">Recipient</p>
               {client ? (
                   <div className="bg-white border-l-4 border-neutral-200 pl-4 py-2">
                       <h3 className="font-serif text-xl font-bold text-neutral-900">{client.name}</h3>
                       <p className="text-neutral-600 text-sm font-medium">{client.company}</p>
                       <p className="text-neutral-500 text-sm mt-1">{client.address}</p>
                   </div>
               ) : <span className="text-neutral-300 italic">No Client Selected</span>}
           </div>

           {/* Content */}
           <div className="flex-1 executive-table">
               {children}
           </div>

           <div className="mt-12 bg-neutral-50 p-6 rounded border border-neutral-200">
               <h4 className="font-bold text-neutral-800 text-sm uppercase tracking-wide mb-2">Notice</h4>
               <p className="text-sm text-neutral-600 font-serif italic">{footerNote}</p>
           </div>
       </div>

       {/* Footer */}
       <div className="bg-neutral-900 text-neutral-500 text-[10px] uppercase tracking-widest text-center py-4">
           Authorized Document • Generated by {profile.name}
       </div>
    </div>
  );
};

export default ExecutiveTemplate;
